import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  SuperadminProfileAddForm,
  SuperadminProfileAddInitialValues,
  SuperadminProfileComponents,
  SuperadminProfileAdditionalParams,
  SuperadminProfileFormOptions,
  onSuperadminProfileFormSaved,
  ISuperadminProfileFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateSuperadminProfile, PermissionEnum, } from 'atlas-shared';
import { createSuperadminProfile, fetchSuperadminProfile, getSuperadminProfileCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateSuperadminProfile> {}

const SuperadminProfileFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateSuperadminProfile>>();

  const param_props: ISuperadminProfileFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout = useMemo(() => SuperadminProfileAddForm(values, param_props, t), [values, organizations, t, abilities, ]);
  const options: IFormOptions = useMemo(() => SuperadminProfileFormOptions(values || SuperadminProfileAddInitialValues, param_props, t), [values, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateSuperadminProfile, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateSuperadminProfile) => save(() => createSuperadminProfile(values).then(
    res => onSaved ? onSaved(res, navigate) : onSuperadminProfileFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchSuperadminProfile(+id)
        .then(({
          title,
          permissions,
        }) => {
          setCloned({
            title,
            permissions,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(null, CrudEnum.Insert, PermissionEnum.SuperadminProfile, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={SuperadminProfileComponents}
        additionalParams={SuperadminProfileAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...SuperadminProfileAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('SUPERADMIN_PROFILE')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const SuperadminProfileFormAddView = React.memo(withAdd(SuperadminProfileFormAdd, getSuperadminProfileCreateSchema, SuperadminProfileAddInitialValues));
